import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "../../api/axios";
import { Table, Badge } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { AllClasses } from "../../configs/allClasses";
import { BillService, AdjustmentService, getCurrentTerm } from "../../services/billService";
import { CircularProgress } from "../../components/custom";
import { useCurrentTermAndSession } from "../../hooks/useCurrentTermAndSession";

const getClassName = (code) => {
  const found = AllClasses.find((c) => c.code === code);
  return found ? found.name : code || "—";
};

const deriveSession = (date) => {
  const d = date ? new Date(date) : new Date();
  const y = d.getFullYear();
  return d.getMonth() + 1 >= 8 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
};

const STATUS_COLORS = {
  draft: "secondary",
  issued: "primary",
  partial: "warning",
  paid: "success",
};

export function FamilyBillDetail() {
  const { parentId } = useParams();
  const navigate = useNavigate();

  const { currentSession, currentTerm, isLoading: hookLoading } = useCurrentTermAndSession();
  const [isInitialized, setIsInitialized] = useState(false);

  const [session, setSession] = useState("");
  const [term, setTerm] = useState("");
  const [parent, setParent] = useState(null);
  const [wards, setWards] = useState([]); // [{student, bills:[]}]
  const [familyBill, setFamilyBill] = useState(null);
  const [loading, setLoading] = useState(true);

  const appUrl = process.env.REACT_APP_URL || "http://localhost:4000/api/v1";
  const token = localStorage.getItem("token");

  // Auto-load current term
  useEffect(() => {
    if (currentSession && currentTerm && !isInitialized) {
      setSession(currentSession);
      setTerm(currentTerm);
      setIsInitialized(true);
    } else if (!hookLoading && !isInitialized) {
      setIsInitialized(true);
    }
  }, [currentSession, currentTerm, hookLoading, isInitialized]);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch parent & FamilyBill in parallel
      const [parentRes, fbRes] = await Promise.all([
        axios.get(`${appUrl}/users/single/${parentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get(
          `/bills/family?session=${session}&term=${term}&parentId=${parentId}`,
        ),
      ]);

      const parentData = parentRes.data?.data || null;
      setParent(parentData);

      const fbData = fbRes.data?.data || [];
      setFamilyBill(fbData[0] || null);

      // If parent could not be found, stop here
      if (!parentData) {
        setWards([]);
        setLoading(false);
        return;
      }

      if (!parentData.wards || parentData.wards.length === 0) {
        setWards([]);
        return;
      }

      // 2. Resolve each ward (admission number → student) and their bills
      const wardResults = await Promise.all(
        parentData.wards.map(async (admissionNumber) => {
          try {
            const stuRes = await axios.get(
              `${appUrl}/users?role=student&admissionNumber=${admissionNumber}&limit=1`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const stuList = stuRes.data.data.list || stuRes.data.data || [];
            const student = stuList[0];
            if (!student) return null;

            const billsRes = await BillService.getBills({
              studentId: student._id,
              session,
              term,
            });
            const bills = billsRes.data || [];

            // ── Fetch adjustments for each bill and attach them ──
            const enrichedBills = await Promise.all(
              bills.map(async (bill) => {
                try {
                  const adjRes = await AdjustmentService.getByBill(bill._id);
                  return { ...bill, adjustments: adjRes.data || [] };
                } catch {
                  return { ...bill, adjustments: [] };
                }
              }),
            );

            return { student, bills: enrichedBills };
          } catch {
            return null;
          }
        }),
      );

      setWards(wardResults.filter(Boolean));
    } catch (err) {
      console.error("Failed to load family data", err);
    } finally {
      setLoading(false);
    }
  }, [appUrl, token, parentId, session, term]);

  useEffect(() => {
    if (parentId && isInitialized && session && term) fetchData();
  }, [parentId, fetchData, isInitialized, session, term]);

  const familyTotal = familyBill?.totalAmount ?? 0;
  const familyPaid = familyBill?.amountPaid ?? 0;
  const familyBalance = familyBill?.balance ?? 0;

  if (loading) {
    return (
      <LoadingWrapper>
        <CircularProgress />
      </LoadingWrapper>
    );
  }

  return (
    <Container>
      {/* ── Breadcrumb / back ─────────────────────────────────────────────── */}
      <BackBtn onClick={() => navigate(-1)}>
        <Icon icon="mdi:arrow-left" width="20" /> Back to Family Bills
      </BackBtn>

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <PageHeader>
        <div>
          <h3>
            {parent?.firstName} {parent?.lastName} — Family Bill
          </h3>
          <p>
            {term} &nbsp;|&nbsp; Academic Session: {session}
          </p>
        </div>

        {/* Term/Session selectors */}
        <TermControls>
          <select value={session} onChange={(e) => setSession(e.target.value)}>
            <option value="2023/2024">2023/2024</option>
            <option value="2024/2025">2024/2025</option>
            <option value="2025/2026">2025/2026</option>
          </select>
          <select value={term} onChange={(e) => setTerm(e.target.value)}>
            <option value="FIRST TERM">FIRST TERM</option>
            <option value="SECOND TERM">SECOND TERM</option>
            <option value="THIRD TERM">THIRD TERM</option>
          </select>
        </TermControls>
      </PageHeader>

      {/* ── Parent info strip ─────────────────────────────────────────────── */}
      {parent && (
        <ParentStrip>
          <InfoChip>
            <Icon icon="mdi:account" width="16" />
            <span>
              {parent.firstName} {parent.lastName}
            </span>
          </InfoChip>
          <InfoChip>
            <Icon icon="mdi:phone" width="16" />
            <span>{parent.tel || "—"}</span>
          </InfoChip>
          <InfoChip>
            <Icon icon="mdi:email-outline" width="16" />
            <span>{parent.email}</span>
          </InfoChip>
          {parent.parentId && (
            <InfoChip>
              <Icon icon="mdi:badge-account" width="16" />
              <span>{parent.parentId}</span>
            </InfoChip>
          )}
          <InfoChip>
            <Icon icon="mdi:account-child" width="16" />
            <span>
              {wards.length} ward{wards.length !== 1 ? "s" : ""}
            </span>
          </InfoChip>
        </ParentStrip>
      )}

      {/* ── Family summary stats ──────────────────────────────────────────── */}
      <StatsRow>
        <StatCard>
          <StatLabel>Total Bill</StatLabel>
          <StatValue>₦{familyTotal.toLocaleString()}</StatValue>
        </StatCard>
        <StatCard color="#155724" bg="#d4edda">
          <StatLabel>Amount Paid</StatLabel>
          <StatValue>₦{familyPaid.toLocaleString()}</StatValue>
        </StatCard>
        <StatCard color="#721c24" bg="#f8d7da">
          <StatLabel>Outstanding Balance</StatLabel>
          <StatValue>₦{familyBalance.toLocaleString()}</StatValue>
        </StatCard>
      </StatsRow>

      {/* ── Per-ward cards in a 3-column grid ────────────────────────────── */}
      {wards.length === 0 ? (
        <EmptyState>
          <Icon icon="mdi:file-document-remove-outline" width="48" color="#ccc" />
          <p>
            No bills found for this family for <strong>{term}</strong> — <strong>{session}</strong>.
          </p>
          <p style={{ fontSize: 13, color: "#aaa", marginTop: 6 }}>
            Bills may not have been generated yet for this term. Go to{" "}
            <em>Generate Bills</em> to create them.
          </p>
        </EmptyState>
      ) : (
        <WardGrid>
          {wards.map(({ student, bills }) => {
            return (
              <WardCard key={student._id}>
                {/* Card header */}
                <WardCardHeader>
                  <Icon icon="mdi:account-school" width="22" color="#1a73e8" />
                  <WardName>
                    <strong>
                      {student.firstName} {student.lastName}
                    </strong>
                    <span>{getClassName(student.currentClass)}</span>
                  </WardName>
                </WardCardHeader>

                {bills.length === 0 ? (
                  <NoBillMsg>No bills for this term.</NoBillMsg>
                ) : (
                  bills.map((bill) => (
                    <div key={bill._id}>
                      <BillStatusBar>
                        <Badge bg={STATUS_COLORS[bill.status] || "secondary"}>
                          {bill.status.toUpperCase()}
                        </Badge>
                        <small>
                          {bill.term} — {bill.session}
                        </small>
                      </BillStatusBar>

                      {/* Fee breakdown */}
                      <Table
                        size="sm"
                        bordered
                        style={{ fontSize: 13, marginBottom: 8 }}
                      >
                        <thead style={{ background: "#f8f9fa" }}>
                          <tr>
                            <th>Description</th>
                            <th style={{ textAlign: "right" }}>Amount (₦)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Regular fee line items */}
                          {(bill.breakdown || []).map((item, idx) => (
                            <tr key={`fee-${idx}`}>
                              <td>{item.description}</td>
                              <td style={{ textAlign: "right", fontWeight: 600 }}>
                                ₦{item.amount.toLocaleString()}
                              </td>
                            </tr>
                          ))}

                          {/* Debit adjustments — extra charges */}
                          {(bill.adjustments || []).filter(a => a.type === "debit").map((adj, idx) => (
                            <tr key={`debit-${idx}`} style={{ background: "#fff8f0" }}>
                              <td style={{ color: "#b45309", fontWeight: 600 }}>
                                ⊕ Extra Charge: {adj.description}
                              </td>
                              <td style={{ textAlign: "right", fontWeight: 700, color: "#b45309" }}>
                                +₦{adj.amount.toLocaleString()}
                              </td>
                            </tr>
                          ))}

                          {/* Credit adjustments — discounts / waivers */}
                          {(bill.adjustments || []).filter(a => a.type === "credit").map((adj, idx) => (
                            <tr key={`credit-${idx}`} style={{ background: "#f0fdf4" }}>
                              <td style={{ color: "#166534", fontWeight: 600 }}>
                                ⊖ Discount / Waiver: {adj.description}
                              </td>
                              <td style={{ textAlign: "right", fontWeight: 700, color: "#166534" }}>
                                −₦{adj.amount.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: "#f1f3f4" }}>
                            <td style={{ fontWeight: 700 }}>Net Total</td>
                            <td style={{ textAlign: "right", fontWeight: 700 }}>
                              ₦{bill.totalAmount.toLocaleString()}
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  ))
                )}
              </WardCard>
            );
          })}
        </WardGrid>
      )}
    </Container>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60vh;
`;

const Container = styled.div`
  padding: 24px;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #1a73e8;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 20px;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  h3 {
    margin: 0;
    color: #222;
  }
  p {
    color: #666;
    margin-top: 4px;
    font-size: 14px;
  }
`;

const TermControls = styled.div`
  display: flex;
  gap: 10px;
  select {
    padding: 8px 14px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    outline: none;
    cursor: pointer;
  }
`;

const ParentStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  padding: 14px 18px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const InfoChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  background: #f1f5ff;
  padding: 4px 12px;
  border-radius: 20px;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${(p) => p.bg || "white"};
  color: ${(p) => p.color || "#222"};
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  min-width: 160px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.75;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
`;

const WardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const WardCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const WardCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
`;

const WardName = styled.div`
  display: flex;
  flex-direction: column;
  strong {
    font-size: 14px;
    color: #222;
  }
  span {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
  }
`;

const BillStatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
  color: #888;
`;

const NoBillMsg = styled.div`
  padding: 20px 16px;
  text-align: center;
  color: #aaa;
  font-size: 13px;
  font-style: italic;
  flex: 1;
`;

const EmptyState = styled.div`
  padding: 60px;
  text-align: center;
  color: #777;
  background: white;
  border-radius: 10px;
  p {
    margin-top: 10px;
    font-size: 16px;
  }
`;
